import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Drawer, type DrawerProps } from '../../../components/ui/Drawer';
import userSettingsService from '../../../services/userSettings.service';

const SETTING_KEY = 'capacity_request_drawer_width';
const MIN_WIDTH = 600;
const availableWidth = () => Math.max(1, window.innerWidth - (window.innerWidth >= 768 ? 64 : window.innerWidth >= 640 ? 40 : 0));
const clamp = (width: number) => Math.max(Math.min(MIN_WIDTH, availableWidth()), Math.min(availableWidth(), width));
const cacheKey = (settingKey: string) => `${userSettingsService.getCacheKey(settingKey)}_drawerWidth`;
function cachedWidth(settingKey: string) {
  try {
    const width = Number(localStorage.getItem(cacheKey(settingKey)));
    return Number.isFinite(width) && width > 0 ? width : null;
  } catch { return null; }
}

type Props = Omit<DrawerProps, 'width' | 'position' | 'containerRef' | 'resizeHandle'> & { initialWidth?: number; settingKey?: string };
export default function CapacityRequestDrawer({ initialWidth = 1100, settingKey = SETTING_KEY, ...props }: Props) {
  const [width, setWidth] = useState(() => clamp(cachedWidth(settingKey) ?? initialWidth));
  const [limit, setLimit] = useState(availableWidth);
  const widthRef = useRef(width);
  const edited = useRef(false);
  const cleanupDrag = useRef<(() => void) | null>(null);
  const apply = useCallback((next: number) => {
    const value = clamp(next);
    widthRef.current = value;
    setWidth(value);
    return value;
  }, []);
  const save = (value: number) => {
    try { localStorage.setItem(cacheKey(settingKey), String(value)); } catch { /* Cache is best effort. */ }
    void userSettingsService.saveUserSetting(settingKey, { width: value }).catch(() => {});
  };

  useEffect(() => {
    if (cachedWidth(settingKey) !== null) return;
    let active = true;
    void userSettingsService.getUserSetting(settingKey).then(setting => {
      const saved = setting?.setting_value?.width;
      if (active && !edited.current && typeof saved === 'number' && Number.isFinite(saved) && saved > 0) {
        const value = apply(saved);
        try { localStorage.setItem(cacheKey(settingKey), String(value)); } catch { /* Best effort. */ }
      }
    }).catch(() => {});
    return () => { active = false; };
  }, [apply, settingKey]);
  useEffect(() => {
    const resize = () => { setLimit(availableWidth()); apply(widthRef.current); };
    window.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); cleanupDrag.current?.(); };
  }, [apply]);

  const startDrag = (event: MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupDrag.current?.();
    edited.current = true;
    const startX = event.clientX;
    const startWidth = widthRef.current;
    const cursor = document.body.style.cursor;
    const selection = document.body.style.userSelect;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const move = (next: globalThis.MouseEvent) => apply(startWidth + startX - next.clientX);
    const cleanup = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', finish);
      window.removeEventListener('blur', finish);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = selection;
      cleanupDrag.current = null;
    };
    const finish = () => { cleanup(); save(widthRef.current); };
    cleanupDrag.current = cleanup;
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', finish);
    window.addEventListener('blur', finish);
  };

  return <Drawer {...props} width={`min(${width}px, 100%)`} resizeHandle={<div
    role="separator" aria-label="Resize capacity request panel" aria-orientation="vertical"
    aria-valuemin={Math.min(MIN_WIDTH, limit)} aria-valuemax={limit} aria-valuenow={Math.round(width)} aria-valuetext={`${Math.round(width)} pixels wide`}
    tabIndex={0} title="Drag to resize · double-click to reset"
    className="absolute inset-y-0 left-0 z-20 hidden w-1 cursor-ew-resize touch-none bg-slate-200 transition-colors hover:bg-blue-400 focus-visible:bg-blue-400 focus-visible:outline-none sm:block dark:bg-gray-700 dark:hover:bg-blue-500"
    onMouseDown={startDrag}
    onDoubleClick={() => { edited.current = true; save(apply(initialWidth)); }}
    onKeyDown={event => {
      const next = event.key === 'ArrowLeft' ? widthRef.current + 40 : event.key === 'ArrowRight' ? widthRef.current - 40 : event.key === 'Home' ? MIN_WIDTH : event.key === 'End' ? availableWidth() : null;
      if (next === null) return;
      event.preventDefault();
      event.stopPropagation();
      edited.current = true;
      save(apply(next));
    }}
  />} />;
}
